import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Plus,
  QrCode,
  Trash2,
  Edit3,
  LogOut,
  Loader2,
  UserPlus,
  X,
  Check,
  Eye,
} from "lucide-react";
import api from "../api/axios";
import BrandLogo from "../components/shared/BrandLogo";

const RELATIONSHIPS = [
  { value: "self", label: "Self", labelHi: "स्वयं" },
  { value: "spouse", label: "Spouse", labelHi: "पत्नी/पति" },
  { value: "parent", label: "Parent", labelHi: "माता-पिता" },
  { value: "child", label: "Child", labelHi: "बच्चा" },
  { value: "other", label: "Other", labelHi: "अन्य" },
];

const FamilyDashboardPage = () => {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [familyGroup, setFamilyGroup] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [registerForm, setRegisterForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ memberName: "", relationship: "self", qrId: "" });
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const getFamilyToken = () => localStorage.getItem("family_token");

  useEffect(() => {
    const token = getFamilyToken();
    if (token) {
      setIsLoggedIn(true);
      loadFamilyData(token);
    }
  }, []);

  const loadFamilyData = async (token) => {
    setLoading(true);
    try {
      const res = await api.get("/family/me", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFamilyGroup(res.data.group);
      setMembers(res.data.members || []);
    } catch {
      localStorage.removeItem("family_token");
      setIsLoggedIn(false);
      setError("Session expired. Please login again.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/family/login", {
        email: loginForm.email,
        password: loginForm.password,
      });
      localStorage.setItem("family_token", res.data.token);
      setFamilyGroup(res.data.group);
      setIsLoggedIn(true);
      loadFamilyData(res.data.token);
    } catch (err) {
      setError(err.response?.data?.error || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError("");
    if (registerForm.password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/family/register", {
        name: registerForm.name,
        email: registerForm.email,
        phone: registerForm.phone,
        password: registerForm.password,
      });
      localStorage.setItem("family_token", res.data.token);
      setFamilyGroup(res.data.group);
      setIsLoggedIn(true);
      loadFamilyData(res.data.token);
    } catch (err) {
      setError(err.response?.data?.error || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!addForm.memberName.trim()) return;
    setLoading(true);
    try {
      const token = getFamilyToken();
      const res = await api.post(
        "/family/members",
        {
          memberName: addForm.memberName,
          relationship: addForm.relationship,
          qrId: addForm.qrId.toUpperCase(),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMembers(res.data.members || []);
      setShowAddModal(false);
      setAddForm({ memberName: "", relationship: "self", qrId: "" });
    } catch (err) {
      setError(err.response?.data?.error || "Failed to add member. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMember = async (memberId) => {
    try {
      const token = getFamilyToken();
      const res = await api.delete(`/family/members/${memberId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMembers(res.data.members || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to remove member. Please try again.");
    } finally {
      setConfirmDeleteId(null);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("family_token");
    setIsLoggedIn(false);
    setFamilyGroup(null);
    setMembers([]);
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="sticky top-0 z-40 bg-white border-b border-slate-200/50 shadow-sm">
          <div className="max-w-lg mx-auto px-4 py-3 flex items-center justify-between gap-3">
            <BrandLogo />
            <div className="flex-1 min-w-0 text-center">
              <p className="text-sm font-bold text-slate-900">Family Dashboard</p>
              <p className="text-xs text-slate-500">परिवार डैशबोर्ड</p>
            </div>
            <div className="w-[72px]" aria-hidden="true" />
          </div>
        </div>

        <div className="max-w-md mx-auto px-4 pt-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-blue-50 rounded-lg flex items-center justify-center mx-auto mb-4 border border-blue-100">
              <Users size={32} className="text-blue-600" />
            </div>
            <h1 className="text-2xl font-extrabold text-slate-900 mb-1">Family Dashboard</h1>
            <p className="text-sm text-slate-600">Manage emergency QR profiles for your entire family</p>
            <p className="text-sm text-slate-500">अपने पूरे परिवार के लिए आपातकालीन QR प्रोफाइल प्रबंधित करें</p>
          </div>

          <div className="bg-white rounded-lg shadow-md border border-slate-200 p-6">
            <div className="flex gap-2 mb-6">
              <button
                onClick={() => setIsRegistering(false)}
                className={`flex-1 py-2.5 text-sm font-bold rounded-lg transition-colors ${
                  !isRegistering ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                Login
              </button>
              <button
                onClick={() => setIsRegistering(true)}
                className={`flex-1 py-2.5 text-sm font-bold rounded-lg transition-colors ${
                  isRegistering ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                Register
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700 font-medium">
                {error}
              </div>
            )}

            {isRegistering ? (
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Your Name</label>
                  <input
                    type="text"
                    value={registerForm.name}
                    onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    placeholder="Head of family name"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={registerForm.email}
                    onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone</label>
                  <input
                    type="tel"
                    value={registerForm.phone}
                    onChange={(e) => setRegisterForm({ ...registerForm, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })}
                    className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    placeholder="10-digit phone number"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password</label>
                  <input
                    type="password"
                    value={registerForm.password}
                    onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    placeholder="Minimum 8 characters"
                    required
                    minLength={8}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3.5 rounded-lg transition-colors disabled:opacity-60"
                >
                  {loading ? "Creating..." : "Create Family Account"}
                </button>
              </form>
            ) : (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={loginForm.email}
                    onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    placeholder="your@email.com"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1.5">Password</label>
                  <input
                    type="password"
                    value={loginForm.password}
                    onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                    className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                    placeholder="Enter your password"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3.5 rounded-lg transition-colors disabled:opacity-60"
                >
                  {loading ? "Logging in..." : "Login"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="sticky top-0 z-40 bg-white border-b border-slate-200/50 shadow-sm">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <BrandLogo />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-slate-900">Family Dashboard</p>
            <p className="text-xs text-slate-500">{familyGroup?.ownerName}'s Family</p>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 px-3 py-2 bg-slate-100 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-200 transition-colors"
          >
            <LogOut size={14} />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 pt-6 pb-8">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700 font-medium">
            {error}
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Family Members</h1>
            <p className="text-sm text-slate-500">{members.length} member{members.length !== 1 ? "s" : ""}</p>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white font-bold text-sm px-4 py-2.5 rounded-lg transition-colors shadow-md shadow-red-600/25"
          >
            <Plus size={16} />
            Add Member
          </button>
        </div>

        {loading && members.length === 0 ? (
          <div className="text-center py-12">
            <Loader2 size={32} className="text-slate-400 mx-auto animate-spin" />
          </div>
        ) : members.length === 0 ? (
          <div className="bg-white rounded-lg border border-slate-200 p-8 text-center shadow-sm">
            <UserPlus size={48} className="text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-700 mb-2">No Family Members Yet</h3>
            <p className="text-sm text-slate-500 mb-4">Add your first family member to get started managing their emergency QR profiles.</p>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center gap-2 bg-slate-900 text-white font-bold text-sm px-6 py-3 rounded-lg hover:bg-slate-800 transition-colors"
            >
              <Plus size={16} />
              Add Family Member
            </button>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {members.map((member) => {
              const rel = RELATIONSHIPS.find((r) => r.value === member.relationship);
              return (
                <div key={member._id} className="bg-white rounded-lg border border-slate-200 p-5 shadow-sm hover:shadow-md transition-colors duration-150">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-11 bg-slate-100 rounded-lg flex items-center justify-center text-lg font-bold text-slate-600">
                        {member.memberName?.charAt(0)?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <p className="text-base font-bold text-slate-900">{member.memberName}</p>
                        <p className="text-xs text-slate-500">{rel?.label || member.relationship} / {rel?.labelHi || ""}</p>
                      </div>
                    </div>
                  </div>

                  {member.qrId && (
                    <div className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 mb-3 border border-slate-100">
                      <QrCode size={14} className="text-slate-500" />
                      <span className="text-xs font-mono font-semibold text-slate-700">{member.qrId}</span>
                    </div>
                  )}

                  <div className="flex gap-2">
                    {member.qrId && (
                      <button
                        onClick={() => navigate(`/${member.qrId}/public`)}
                        className="flex-1 flex items-center justify-center gap-1.5 bg-teal-50 text-teal-700 font-semibold text-xs py-2 rounded-lg border border-teal-100 hover:bg-teal-100 transition-colors"
                      >
                        <Eye size={12} />
                        View
                      </button>
                    )}
                    {member.qrId && (
                      <button
                        onClick={() => navigate(`/${member.qrId}/info`)}
                        className="flex-1 flex items-center justify-center gap-1.5 bg-blue-50 text-blue-700 font-semibold text-xs py-2 rounded-lg border border-blue-100 hover:bg-blue-100 transition-colors"
                      >
                        <Edit3 size={12} />
                        Edit
                      </button>
                    )}
                    {confirmDeleteId === member._id ? (
                      <>
                        <button
                          onClick={() => handleDeleteMember(member._id)}
                          className="flex items-center justify-center gap-1 bg-red-600 text-white font-bold text-xs py-2 px-2.5 rounded-lg hover:bg-red-700 transition-colors"
                        >
                          <Check size={12} /> Confirm
                        </button>
                        <button
                          onClick={() => setConfirmDeleteId(null)}
                          className="flex items-center justify-center bg-slate-100 text-slate-700 font-semibold text-xs py-2 px-2.5 rounded-lg hover:bg-slate-200 transition-colors"
                        >
                          <X size={12} />
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => setConfirmDeleteId(member._id)}
                        className="flex items-center justify-center gap-1.5 bg-red-50 text-red-700 font-semibold text-xs py-2 px-3 rounded-lg border border-red-100 hover:bg-red-100 transition-colors"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-md max-w-md w-full">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-900">Add Family Member</h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1.5 bg-slate-100 rounded-lg hover:bg-slate-200 transition-colors"
              >
                <X size={18} className="text-slate-600" />
              </button>
            </div>

            <form onSubmit={handleAddMember} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Member Name</label>
                <input
                  type="text"
                  value={addForm.memberName}
                  onChange={(e) => setAddForm({ ...addForm, memberName: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                  placeholder="Enter name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Relationship</label>
                <select
                  value={addForm.relationship}
                  onChange={(e) => setAddForm({ ...addForm, relationship: e.target.value })}
                  className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900 bg-white"
                >
                  {RELATIONSHIPS.map((rel) => (
                    <option key={rel.value} value={rel.value}>
                      {rel.label} / {rel.labelHi}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">QR Code ID (optional)</label>
                <input
                  type="text"
                  value={addForm.qrId}
                  onChange={(e) => setAddForm({ ...addForm, qrId: e.target.value.toUpperCase() })}
                  className="w-full border border-slate-300 rounded-lg px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-900"
                  placeholder="Enter QR code ID (e.g., ABC123)"
                />
                <p className="text-xs text-slate-500 mt-1">Link to an existing QR sticker</p>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 bg-slate-100 text-slate-700 font-semibold py-3 rounded-lg hover:bg-slate-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold py-3 rounded-lg transition-colors"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                  Add Member
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FamilyDashboardPage;
